import React, { useEffect, useState } from 'react';
import { Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import CheckIcon from '@material-ui/icons/Check';
import { isEmail } from '../../utils/tools';
import Input from '../Input';
import Modal from '../Modal';
import { useStyles } from './ClientExportModal.styles';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  totalClients: number;
  shopName: string;
  onClick: (email: string) => Promise<boolean>;
};

const ClientExportModal = ({
  isOpen,
  onClose,
  totalClients,
  shopName,
  onClick,
}: Props) => {
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setEmailTouched(false);
      setShowSuccess(false);
      setSubmitError(false);
    }
  }, [isOpen]);

  const hasValidEmail = isEmail(email.trim());
  const showEmailError = emailTouched && !hasValidEmail;

  const handleContinue = async () => {
    setEmailTouched(true);
    if (!hasValidEmail) {
      return;
    }

    setSubmitError(false);
    setIsSubmitting(true);
    try {
      const succeeded = await onClick(email);
      if (!succeeded) {
        setShowSuccess(true);
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      title="Exporter la liste des clients"
      header
      fixed
      width="560px"
      showCancelBtn={!showSuccess}
      btnCloseTxt="Annuler"
      btnTxt={showSuccess ? 'Ok' : 'Valider'}
      onClick={showSuccess ? onClose : handleContinue}
      loading={isSubmitting}
      disabled={!showSuccess && (!hasValidEmail || isSubmitting)}
    >
      {showSuccess ? (
        <div className={classes.preview}>
          <div className={classes.previewIcon}>
            <CheckIcon aria-hidden="true" />
          </div>
          <Typography className={classes.previewTitle}>Export lancé</Typography>
          <Typography className={classes.previewText}>
            Le fichier des {totalClients.toLocaleString('fr-FR')} clients sera
            envoyé à
          </Typography>
          <Typography className={classes.previewEmail}>{email.trim()}</Typography>
          <Typography className={classes.previewText}>d'ici quelques minutes.</Typography>
          <Typography component="div" className={classes.previewHint}>
            Rien reçu ? Pensez à vérifier vos spams, ou{' '}
            <button
              type="button"
              className={classes.retryButton}
              onClick={() => setShowSuccess(false)}
            >
              relancez l'export
            </button>
            .
          </Typography>
        </div>
      ) : (
        <>
          <div className={classes.summary}>
            <div className={classes.summaryIcon}>
              <PeopleOutlineIcon />
            </div>
            <div>
              <Typography className={classes.summaryTitle}>
                {totalClients.toLocaleString('fr-FR')} clients seront exportés
              </Typography>
              <Typography className={classes.summaryDetails}>
                Magasin : {shopName} · Fichier CSV (compatible Excel)
              </Typography>
            </div>
          </div>

          <Input
            id="client-export-email"
            type="email"
            label="Adresse email de réception *"
            placeholder="nom@domaine.fr"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onBlur={() => setEmailTouched(true)}
            fullWidth
            variant="standard"
            error={showEmailError}
            helperText={showEmailError ? 'Adresse email invalide — exemple : nom@domaine.fr' : ''}
          />
          <Typography className={`${classes.helperText} ${showEmailError ? classes.error : ''}`}>
            {!showEmailError &&
              'Le fichier vous sera envoyé par email sous quelques minutes.'}
          </Typography>
          {submitError && (
            <Alert severity="error" className={classes.submitError}>
              L'export n'a pas pu être lancé. Vérifiez votre connexion puis réessayez.
            </Alert>
          )}
        </>
      )}
    </Modal>
  );
};

export default ClientExportModal;
